import React from "react";
import { Input } from "../../../common/u1/Input";
import { FileUpload } from "../../../common/u1/FileUpload";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { SelectField } from "../../../common/u1/SelectField";
import { cn } from "../../../common/u1/cn";
import { REASON_MAX, REASON_MIN } from "../../../../Hooks/u1/useClaimDispute";

/* ICC-11 — raising a dispute on a company someone else has claimed.
 *
 * This is NOT a claim step: it replaces the wizard rather than extending it,
 * because nothing here transfers the company. It opens a support ticket.
 */
export const DisputeStep = ({
  companyName = "",
  fullName = "",
  onFullNameChange = () => {},
  contactEmail = "",
  onContactEmailChange = () => {},
  designation = "",
  onDesignationChange = () => {},
  phone = "",
  onPhoneChange = () => {},
  dialCountry = "AE",
  onDialCountryChange = () => {},
  dialCodeOptions = [],
  reason = "",
  onReasonChange = () => {},
  evidence = null,
  onEvidenceChange = () => {},
  errors = {},
}) => {
  const over = reason.length > REASON_MAX;

  return (
    <div className="grid gap-3.5">
      <p className="text-[14px] leading-[1.55] text-[var(--ink-2)] font-sans">
        {companyName ? <b>{companyName}</b> : "This company"} has already been
        claimed on TREVEX. If the profile is yours, tell us why and our support
        team will look into it. The current owner is not told who raised this.
      </p>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <Input
          id="dispute-full-name"
          label="Your Name"
          labelVariant="caption"
          required
          value={fullName}
          onChange={(e) => onFullNameChange(e.target.value)}
          placeholder="Full name"
          error={errors.fullName}
        />
        <Input
          id="dispute-email"
          label="Email Address"
          labelVariant="caption"
          required
          type="email"
          value={contactEmail}
          onChange={(e) => onContactEmailChange(e.target.value)}
          placeholder="you@company.ae"
          hint="Support replies to this address."
          error={errors.contactEmail}
        />
      </div>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <Input
          id="dispute-designation"
          label="Job Title"
          labelVariant="caption"
          value={designation}
          onChange={(e) => onDesignationChange(e.target.value)}
          placeholder="e.g. Managing Director"
          error={errors.designation}
        />
        <div>
          {/* A span, not a label: the row below is two controls, and a label
              can only point at one of them. Each carries its own accessible
              name. Same shape as the claim flow's contact step. */}
          <FieldLabel as="span" variant="caption">
            Contact number
          </FieldLabel>
          {/* No flag: the country code and the dialling code are the two
              things that identify the number, and 130px with a flag in front
              of it truncated them to "AF (+…". */}
          <div className="mt-1.5 grid gap-2.5 sm:grid-cols-[150px_1fr]">
            <SelectField
              id="dispute-dial-code"
              aria-label="Country dial code"
              value={dialCountry}
              onChange={(e) => onDialCountryChange(e.target.value)}
              options={dialCodeOptions}
            />
            <Input
              id="dispute-phone"
              aria-label="Contact number"
              inputMode="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="50 123 4567"
              error={errors.phone}
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between gap-3">
          <FieldLabel htmlFor="dispute-reason" required variant="caption">
            Why are you the rightful owner?
          </FieldLabel>
          <span
            className={cn(
              "text-[12px] font-sans",
              over ? "text-[var(--coral-deep)]" : "text-[var(--muted)]"
            )}
          >
            {reason.length}/{REASON_MAX}
          </span>
        </div>

        <textarea
          id="dispute-reason"
          rows={5}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Tell us your role at the company and anything that shows the profile belongs to you."
          className={cn(
            "mt-2 block w-full rounded-xl border bg-white px-3.5 py-3",
            "text-[14px] text-[var(--ink)] font-sans outline-none transition-colors",
            "placeholder:text-[var(--muted-2)]",
            over
              ? "border-[var(--coral-deep)]"
              : "border-[var(--line)] focus:border-[var(--orange)]"
          )}
        />
        <p className="mt-1.5 text-[12.5px] text-[var(--muted)] font-sans">
          At least {REASON_MIN} characters — the more detail, the faster support
          can resolve it.
        </p>
      </div>

      <FileUpload
        id="dispute-evidence"
        label="Supporting Document"
        value={evidence}
        onChange={onEvidenceChange}
        hint="Optional. PDF, JPG, or PNG — a trade licence or authority letter helps."
      />
    </div>
  );
};

export default DisputeStep;
